import { useMemo } from 'react';
import { useQuery } from '@common-fe/common-fe';

import { api } from '@/api';
import { API_URL } from '@/dtos/generated-paths';
import useEmployeeStore from '@/modules/user/stores/useEmployee.store';

interface AddressInfo {
  id?: number;
  line1?: string;
  line2?: string;
  city?: string;
  state?: string;
  zipcode?: string;
  countryId?: number;
  undeliverable?: boolean;
}

interface MailingAddressInfo {
  address: AddressInfo;
  preferredMailing?: boolean;
}

interface PersonalInfoResponse {
  mailing_address?: {
    address?: {
      id?: number;
      line1?: string;
      line2?: string;
      city?: string;
      state?: string;
      zipcode?: string;
      country_id?: number;
      undeliverable?: boolean;
    };
    preferred_mailing?: boolean;
  };
}

const QUERY_KEY = 'EMPLOYEE_PERSONAL_INFO_QUERY';

const useEmployeePersonalInfoQuery = () => {
  const { employee } = useEmployeeStore();
  const employeeId = employee?.id;

  const { data, isLoading, isSuccess, refetch } = useQuery(
    [QUERY_KEY, employeeId],
    () => api.get<PersonalInfoResponse>(API_URL.EMPLOYEES_PERSONAL_INFO_BY_EMPLOYEE_ID(employeeId!)),
    {
      enabled: !!employeeId,
    }
  );

  const mailingAddress = useMemo((): MailingAddressInfo | undefined => {
    const raw = data?.data?.mailing_address;
    if (!raw?.address) return undefined;

    return {
      address: {
        id: raw.address.id,
        line1: raw.address.line1,
        line2: raw.address.line2,
        city: raw.address.city,
        state: raw.address.state,
        zipcode: raw.address.zipcode,
        countryId: raw.address.country_id,
        undeliverable: raw.address.undeliverable ?? false,
      },
      preferredMailing: raw.preferred_mailing ?? false,
    };
  }, [data]);

  return { mailingAddress, isLoading, isSuccess, refetch };
};

export default useEmployeePersonalInfoQuery;
export { QUERY_KEY as EMPLOYEE_PERSONAL_INFO_QUERY_KEY };
